import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Settings, Sun, Moon, Smile, Info } from "lucide-react";
import SimpleDropdown from "../Common/SimpleDropdown";
import { useAuth } from "../Auth/AuthContext";
import { Link } from "react-router-dom";
import CommonApiService from '../../ApiServices/CommonApiService';
import moment from "moment";

const Topbar = () => {
    const dropdownRef = useRef(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [time, setTime] = useState(new Date().toLocaleTimeString());
    const [noteTitle, setNoteTitle] = useState('');
    const [noteDescription, setNoteDescription] = useState('');
    const [showDescription, setShowDescription] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [showNotifications, setShowNotifications] = useState(false);

    const Auth = useAuth();
    const user = Auth.authState.user;
    const role = Auth.authState.roles;
    const admin = role === "Admin";

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowNotifications(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        fetchNotes();
        fetchNotifications(); // Fetch immediately on mount

        // setInterval(fetchNotifications, 10000);

        const timeInterval = setInterval(() => {
            setTime(new Date().toLocaleTimeString());
        }, 1000); // Update time every second

        return () => {
            clearInterval(timeInterval); // Cleanup clock update
        };
    }, []);

    const toggleDarkMode = () => {
        setIsDarkMode(!isDarkMode);
        document.documentElement.classList.toggle("dark");
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return { message: "Good Morning", icon: <Sun size={20} className="text-yellow-500" /> };
        if (hour < 18) return { message: "Good Afternoon", icon: <Smile size={20} className="text-orange-500" /> };
        return { message: "Good Evening", icon: <Moon size={20} className="text-blue-500" /> };
    };

    const { message, icon } = getGreeting();

    const fetchNotes = async () => {
        try {
            const note = await CommonApiService.getNote();
            setNoteTitle(note.title);
            setNoteDescription(note.description);
        } catch (error) {
            console.error('Error fetching notes:', error.message);
        }
    };

    const fetchNotifications = async () => {
        try {
            const response = await CommonApiService.getAllNotifications();
            setNotifications(response || []);
        } catch (error) {
            console.error('Error fetching notifications:', error.message);
        }
    };

    const updateNotifications = async () => {
        try {
            await CommonApiService.updateNotifications();
        } catch (error) {
            console.error('Error fetching notifications:', error.message);
        }
    };

    function handleNotification(item) {
        setShowNotifications(item);
        setTimeout(() => {
            updateNotifications();
        }, 5000);
    }

    return (
        <motion.header
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`shadow-md p-4 flex items-center justify-between fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${isDarkMode ? "bg-gray-900 text-white" : "bg-white text-gray-900"}`}
        >
            {/* Dashboard Title */}
            <motion.h3
                className="text-2xl md:text-3xl font-bold text-purple-700 capitalize"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4 }}
            >
                
            </motion.h3>

            {/* Greeting with Clock */}
            <motion.div
                className="hidden md:flex items-center gap-4 bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                {icon}
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    {message}, <strong>{user?.firstName}!</strong>
                </span>
                <span className="text-gray-500 dark:text-gray-400 font-mono">{time}</span>
            </motion.div>

            {/* Notes Section */}
            <motion.div
                className="relative hidden md:flex items-center gap-4 bg-gray-100 dark:bg-gray-800 px-4 py-2 rounded-lg shadow-sm cursor-pointer"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                onMouseEnter={() => setShowDescription(true)}
                onMouseLeave={() => setShowDescription(false)}
            >
                <Info size={20} />
                <span className="text-gray-700 dark:text-gray-300 font-medium">
                    <strong>{noteTitle ?? "No Title"}</strong>
                </span>
                {showDescription && (
                    <motion.div
                        className="absolute top-10 left-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white p-4 rounded-lg shadow-lg w-64"
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <p className="text-sm font-medium leading-relaxed">
                            {noteDescription ?? "No description available."}
                        </p>
                    </motion.div>
                )}
            </motion.div>

            {/* Navigation */}
            <nav className="relative flex items-center gap-4">
                {/* Dark Mode Toggle */}
                <motion.button
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full shadow-md hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={toggleDarkMode}
                >
                    {isDarkMode ? <Sun size={22} className="text-yellow-500" /> : <Moon size={22} className="text-blue-500" />}
                </motion.button>

                {/* Settings Button */}
                <motion.button
                    className="p-2 bg-gray-100 dark:bg-gray-700 rounded-full shadow-md hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <Link to="/adminsetting">
                        <Settings size={22} className="text-gray-600 dark:text-gray-300" />
                    </Link>
                </motion.button>
                {admin ? (
                    <div className="relative" ref={dropdownRef}>
                        {/* Notification Bell */}
                        <motion.button
                            className="relative p-3 bg-gray-100 dark:bg-gray-800 rounded-full shadow-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={handleNotification}
                        >
                            <Bell size={24} className="text-gray-700 dark:text-gray-300" />
                            {notifications.some((notif) => !notif.isRead) && (
                                <motion.span
                                    className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full shadow"
                                    animate={{ scale: [1, 1.2, 1] }}
                                    transition={{ repeat: Infinity, duration: 1.5 }}
                                >
                                    {notifications.filter((notif) => !notif.isRead).length}
                                </motion.span>
                            )}
                        </motion.button>

                        {/* Dropdown Content */}
                        <AnimatePresence>
                            {showNotifications && (
                                <motion.div
                                    className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 rounded-lg shadow-2xl p-4 z-50 border dark:border-gray-700"
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Notifications</h4>
                                    <div className="max-h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                                        {notifications.length === 0 ? (
                                            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-3">
                                                No new notifications
                                            </p>
                                        ) : (
                                            notifications.map((notif) => (
                                                <motion.div
                                                    key={notif.id}
                                                    className={`flex items-start p-4 my-2 rounded-lg cursor-pointer transition-all shadow-sm border ${notif.isRead
                                                        ? "bg-gray-50 dark:bg-gray-800"
                                                        : "bg-blue-50 dark:bg-blue-900"
                                                        } hover:bg-gray-200 dark:hover:bg-gray-700`}
                                                    whileHover={{ scale: 1.02 }}
                                                >
                                                    <div className="flex-1">
                                                        <p className="text-gray-800 dark:text-gray-200 text-sm font-medium">
                                                            {notif.title}
                                                        </p>
                                                        <p className="text-gray-500 dark:text-gray-400 text-xs">{notif.message}</p>
                                                        <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                                                            {moment(notif.createdAt).fromNow()}
                                                        </p>
                                                    </div>
                                                </motion.div>
                                            ))
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                ) : ""}
                <SimpleDropdown />
            </nav>
        </motion.header>
    );
};

export default Topbar;
